import React, { useState, useEffect } from 'react';
import { WorkoutType } from '../types/workout';
import {
  WorkoutResult,
  AMRAPResult,
  ForTimeResult,
  EMOMResult,
  MaxTimeResult,
  MaxWeightResult,
  getResultTypeFromWorkoutType
} from '../types/result';

interface ResultInputFormProps {
  workoutType: WorkoutType;
  existingResult?: WorkoutResult;
  onSave: (result: WorkoutResult) => void;
  competitorName: string;
}

const ResultInputForm: React.FC<ResultInputFormProps> = ({
  workoutType,
  existingResult,
  onSave,
  competitorName
}) => {
  // AMRAP state
  const [reps, setReps] = useState<number>(0);

  // For Time state
  const [mins, setMins] = useState<number | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  const [forTimeReps, setForTimeReps] = useState<number | null>(null);
  const [cap, setCap] = useState<boolean>(false);

  // EMOM state
  const [roundsCompleted, setRoundsCompleted] = useState<number>(0);
  const [lastRoundReps, setLastRoundReps] = useState<number | null>(null);

  // Max Time state
  const [maxMins, setMaxMins] = useState<number>(0);
  const [maxSeconds, setMaxSeconds] = useState<number>(0);

  // Max Weight state
  const [weight, setWeight] = useState<number>(0);

  useEffect(() => {
    if (existingResult) {
      switch (existingResult.type) {
        case 'AMRAP':
          setReps((existingResult as AMRAPResult).reps);
          break;
        case 'FOR_TIME':
          const ftResult = existingResult as ForTimeResult;
          setMins(ftResult.mins);
          setSeconds(ftResult.seconds);
          setForTimeReps(ftResult.reps);
          setCap(ftResult.cap);
          break;
        case 'EMOM':
          const emomResult = existingResult as EMOMResult;
          setRoundsCompleted(emomResult.roundsCompleted);
          setLastRoundReps(emomResult.lastRoundReps);
          break;
        case 'MAX_TIME':
          const maxTimeResult = existingResult as MaxTimeResult;
          setMaxMins(maxTimeResult.mins);
          setMaxSeconds(maxTimeResult.seconds);
          break;
        case 'MAX_WEIGHT':
          setWeight((existingResult as MaxWeightResult).weight);
          break;
      }
    }
  }, [existingResult]);

  const handleSave = () => {
    let result: WorkoutResult;

    switch (workoutType) {
      case WorkoutType.AMRAP:
        result = {
          type: 'AMRAP',
          reps
        };
        break;
      case WorkoutType.FOR_TIME:
        result = {
          type: 'FOR_TIME',
          mins,
          seconds,
          reps: forTimeReps,
          cap
        };
        break;
      case WorkoutType.EMOM:
        result = {
          type: 'EMOM',
          roundsCompleted,
          lastRoundReps
        };
        break;
      case WorkoutType.MAX_TIME:
        result = {
          type: 'MAX_TIME',
          mins: maxMins,
          seconds: maxSeconds
        };
        break;
      case WorkoutType.MAX_WEIGHT:
        result = {
          type: 'MAX_WEIGHT',
          weight
        };
        break;
      default:
        return;
    }

    onSave(result);
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{competitorName}</h4>

      {workoutType === WorkoutType.AMRAP && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Total Reps</label>
            <input
              type="number"
              value={reps}
              onChange={(e) => setReps(parseInt(e.target.value) || 0)}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
      )}

      {workoutType === WorkoutType.FOR_TIME && (
        <div className="space-y-3">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id={`cap-${competitorName}`}
              checked={cap}
              onChange={(e) => setCap(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor={`cap-${competitorName}`} className="ml-2 text-sm text-gray-700">
              Time Cap (DNF)
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Minutes</label>
              <input
                type="number"
                value={mins ?? ''}
                onChange={(e) => setMins(e.target.value ? parseInt(e.target.value) : null)}
                min="0"
                placeholder="0"
                disabled={cap}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Seconds</label>
              <input
                type="number"
                value={seconds ?? ''}
                onChange={(e) => setSeconds(e.target.value ? parseInt(e.target.value) : null)}
                min="0"
                max="59"
                placeholder="0"
                disabled={cap}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {cap ? 'Reps Completed' : 'Reps (if not finished)'}
            </label>
            <input
              type="number"
              value={forTimeReps ?? ''}
              onChange={(e) => setForTimeReps(e.target.value ? parseInt(e.target.value) : null)}
              min="0"
              placeholder={cap ? 'Reps at time cap' : 'Leave empty if completed'}
              disabled={!cap}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      )}

      {workoutType === WorkoutType.EMOM && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Rounds Completed</label>
            <input
              type="number"
              value={roundsCompleted}
              onChange={(e) => setRoundsCompleted(parseInt(e.target.value) || 0)}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Last Round Reps</label>
            <input
              type="number"
              value={lastRoundReps ?? ''}
              onChange={(e) => setLastRoundReps(e.target.value ? parseInt(e.target.value) : null)}
              min="0"
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
      )}

      {workoutType === WorkoutType.MAX_TIME && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Minutes</label>
              <input
                type="number"
                value={maxMins}
                onChange={(e) => setMaxMins(parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Seconds</label>
              <input
                type="number"
                value={maxSeconds}
                onChange={(e) => setMaxSeconds(parseInt(e.target.value) || 0)}
                min="0"
                max="59"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {workoutType === WorkoutType.MAX_WEIGHT && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weight (lbs)</label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        className="mt-3 w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-sm font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-all"
      >
        Save Result
      </button>
    </div>
  );
};

export default ResultInputForm;
